// Page-side glue over the persistence seam: one mount read, fire-and-forget
// writes, and the small amount of state the save surface renders.
//
// Shared by both funnel pages so the buyer and seller tracks cannot drift on
// when a draft is created or what a conflict looks like.

import { useCallback, useEffect, useRef, useState } from "react";
import type { PrequalFinalizeRequest, PrequalFinalizeResult } from "../../api/prequalification";
import type {
  PrequalDraftAdapter,
  PrequalDraftLoad,
  PrequalDraftSnapshot,
} from "./prequalDraftAdapter";

export interface PrequalDraftSessionOptions {
  adapter: PrequalDraftAdapter;
  /**
   * True when this tab restored the visitor's own answers from sessionStorage.
   *
   * The local blob then WINS over the server copy, and that is the safe
   * direction: every server write in this feature is preceded by a local one,
   * so the local state is never older — but it can easily be newer (answers
   * typed while the network was down are in sessionStorage and nowhere else).
   * Hydrating over it would silently discard them.
   */
  hasLocalState: boolean;
  /** Apply a resumed draft to the form. Held in a ref, so the caller may pass a
   *  fresh closure every render. */
  onLoaded: (loaded: PrequalDraftLoad) => void;
}

export interface PrequalDraftSession {
  /** True once a server draft backs this session. */
  saved: boolean;
  /** The resume URL, verbatim from the server, on the organic path only. */
  resumeUrl: string | null;
  /** Somebody else wrote to this draft — the form offers a reload. */
  conflict: boolean;
  /** A renderable message for the last failed write, else null. */
  error: string | null;
  saving: boolean;
  /** Fire-and-forget. Never blocks navigation: the funnel keeps its
   *  sessionStorage fallback, so a failed save costs nothing the next Continue
   *  does not re-send (every write carries the WHOLE answers blob, never a
   *  delta). */
  persist: (snapshot: PrequalDraftSnapshot) => void;
  /** Re-read after a conflict — the one place a load overrides local state. */
  reload: () => void;
  finalize: (request: PrequalFinalizeRequest) => Promise<PrequalFinalizeResult>;
}

export function usePrequalDraftSession(options: PrequalDraftSessionOptions): PrequalDraftSession {
  const { adapter, hasLocalState } = options;

  const [saved, setSaved] = useState(false);
  const [resumeUrl, setResumeUrl] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onLoadedRef = useRef(options.onLoaded);
  onLoadedRef.current = options.onLoaded;

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const applyLoaded = useCallback(
    (loaded: PrequalDraftLoad | null, applyToForm: boolean) => {
      if (!mounted.current || loaded === null) return;
      setSaved(true);
      if (applyToForm) onLoadedRef.current(loaded);
    },
    [],
  );

  // ONE read on mount. A read, and only a read: this is the request every mail
  // scanner, link-preview bot and prefetch makes on an invited link, so it must
  // never be the thing that creates the row. StrictMode's double-invoke is
  // covered by the ref rather than by the request being harmless.
  const loadedOnce = useRef(false);
  useEffect(() => {
    if (loadedOnce.current) return;
    loadedOnce.current = true;
    void adapter.load().then((loaded) => applyLoaded(loaded, !hasLocalState));
    // hasLocalState is read once, at mount, on purpose — a blob saved later in
    // this same session must not change whether the initial read applied.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter, applyLoaded]);

  const inFlight = useRef(false);
  const queued = useRef<PrequalDraftSnapshot | null>(null);

  const run = useCallback(
    async (snapshot: PrequalDraftSnapshot) => {
      inFlight.current = true;
      if (mounted.current) setSaving(true);
      try {
        const result = await adapter.persistStep(snapshot);
        if (!mounted.current) return;
        if (result.status === "saved") {
          setSaved(true);
          setConflict(false);
          setError(null);
          // Emitted exactly once, on the organic create. Rendering it is the
          // only thing standing between this visitor and a permanent dead end:
          // there is no email fallback by design.
          if (result.resumeUrl) setResumeUrl(result.resumeUrl);
        } else if (result.status === "conflict") {
          setConflict(true);
          setError(result.message ?? null);
        } else if (result.status === "failed") {
          setError(result.message ?? null);
        }
      } finally {
        inFlight.current = false;
        const next = queued.current;
        queued.current = null;
        if (mounted.current) {
          setSaving(false);
          if (next !== null) void run(next);
        }
      }
    },
    [adapter],
  );

  const persist = useCallback(
    (snapshot: PrequalDraftSnapshot) => {
      // A write already in flight: keep the LATEST snapshot and send it after.
      // Dropping it would be nearly harmless (the next Continue re-sends
      // everything) but "nearly" is exactly the visitor who then abandons.
      if (inFlight.current) {
        queued.current = snapshot;
        return;
      }
      void run(snapshot);
    },
    [run],
  );

  const reload = useCallback(() => {
    void adapter.load().then((loaded) => {
      if (!mounted.current) return;
      setConflict(false);
      setError(null);
      applyLoaded(loaded, true);
    });
  }, [adapter, applyLoaded]);

  const finalize = useCallback(
    (request: PrequalFinalizeRequest) => adapter.finalize(request),
    [adapter],
  );

  return { saved, resumeUrl, conflict, error, saving, persist, reload, finalize };
}
